import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiHeader, uiKpis, uiPays } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Consequences: the world remembers what you decided (backend/consequences.py).
 *
 *  - Waiting for you: each chapter as a SCENE: the setting (meeting room,
 *    press conference, boardroom, town hall, phone call), the person, their
 *    words appearing line by line, then your choices as centred buttons. The
 *    reaction and the real effects show the moment you choose.
 *  - Your stories: every running chain as a timeline: what you did first,
 *    each choice since, and a "?" for the chapter still to come, with the
 *    trust of the country or company involved. A story that ends is deleted
 *    (no history kept); only the counts above remember it.
 *  - Your word: the reputation that outlives the stories (promises kept or
 *    broken, the truth told or denied). New stories bring it up in a line of
 *    their own (.cq-echo).
 */
const CQ_SCENE_ICON = { meeting: "🏛", press: "🎤", boardroom: "💼", town_hall: "📣", phone: "📞" };
const CQ_COLORS = ["#7ee8fa", "#f6b26b", "#b39ddb", "#78dca0", "#f48fb1", "#ffd54f"];

function cqInitials(name) {
  const clean = String(name || "?").split(",")[0].replace(/^(Minister|Your)\s+/i, "");
  return clean.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
}

function cqColor(name) {
  let h = 0;
  for (const ch of String(name)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CQ_COLORS[h % CQ_COLORS.length];
}

function cqScene(w) {
  return `<article class="cq-scene cq-${esc(w.scene)}" data-chain="${w.chain_id}">
    <div class="cq-stage-top"><span class="cq-place">${CQ_SCENE_ICON[w.scene] || "💬"} ${esc(w.scene_label)}</span>
      <span class="cq-story">${esc(w.story)}${w.subject ? ` · ${esc(w.subject)}` : ""}</span>
      <span class="cq-due ${w.months_left === 0 ? "now" : ""}">${w.months_left === 0 ? "Answer this month" : `Answer by month ${w.deadline}`}</span></div>
    <h3 class="cq-title">${esc(w.title)}</h3>
    <div class="cq-talk">
      <div class="cq-face" style="--c:${cqColor(w.who)}">${esc(cqInitials(w.who))}</div>
      <div class="cq-lines"><b class="cq-who">${esc(w.who)}</b>
        ${w.lines.map((l, i) => `<p class="cq-line${l === w.echo ? " cq-echo" : ""}" style="--i:${i}">${esc(l)}</p>`).join("")}</div>
    </div>
    <div class="cq-choices">${w.options.map((o) => `
      <div class="cq-choice${o.affordable === false ? " short" : ""}"><button type="button" class="cq-btn" data-cq-chain="${w.chain_id}" data-cq-option="${esc(o.key)}"${o.affordable === false ? " disabled" : ""}>${esc(o.label)}</button>
        <p>${esc(o.detail)}</p>${uiPays(o)}${o.affordable === false ? `<p class="cq-short">Not enough cash for this one</p>` : ""}</div>`).join("")}</div>
    <p class="cq-ignore">If you don't answer: <b>${esc(w.if_ignored)}</b></p>
    <div class="cq-result" data-cq-result></div>
  </article>`;
}

const CQ_MARK = { kept: ["🤝", "Kept a promise"], honest: ["🗣", "Told the truth"], broke: ["💔", "Broke a promise"], lied: ["🎭", "Denied, then got caught"] };

function cqWord(w) {
  if (!w) return "";
  const tone = w.broke === 0 && w.kept ? "good" : w.broke > w.kept ? "bad" : "";
  const marks = w.last.length ? `<ul class="cq-marks">${w.last.map((m) => `<li class="${m.tag}"><span>${CQ_MARK[m.tag][0]}</span>
      <b>${esc(CQ_MARK[m.tag][1])}${m.where ? ` in ${esc(m.where)}` : ""}</b><i>M${m.month}</i></li>`).join("")}</ul>`
    : `<p class="cq-how">Keep a promise, or break one. The next minister, union leader or investor will have heard.</p>`;
  return uiCard("Your word", `<div class="cq-word ${tone}"><strong>${esc(w.verdict)}</strong>
      <span>🤝 ${w.kept} kept</span><span>💔 ${w.broke} broken</span></div>${marks}`,
    { sub: "Stories end and are cleared. What people remember about you stays." });
}

function cqTimeline(s) {
  const nodes = s.history.map((h) => h.kind === "origin"
    ? `<li class="origin"><i>M${h.month}</i><b>${esc(h.title)}</b></li>`
    : `<li class="${h.auto ? "auto" : ""}"><i>M${h.month}</i><b>${CQ_SCENE_ICON[h.scene] || ""} ${esc(h.title)}</b>
        <span>${h.auto ? "Decided for you: " : "You: "}${esc(h.choice)}</span>${h.reaction ? `<em>“${esc(h.reaction.replace(/^"|"$/g, ""))}”</em>` : ""}
        ${(h.effects || []).length ? `<small>${h.effects.map(esc).join(" ")}</small>` : ""}</li>`).join("");
  const next = s.waiting_now ? `<li class="next now"><i>Now</i><b>Waiting for your answer above</b></li>`
    : `<li class="next"><i>?</i><b>${esc(s.next_hint)}</b></li>`;
  const trust = s.trust == null ? "" : `<div class="cq-trust"><span>${esc(s.subject)} trusts you</span><div><i style="width:${s.trust}%;background:${s.trust >= 65 ? "#78dca0" : s.trust >= 40 ? "#ffd54f" : "#ef6f6c"}"></i></div><b>${Math.round(s.trust)}/100</b></div>`;
  return `<section class="cq-tl ${s.status}"><div class="cq-tl-head"><b>${esc(s.label)}</b>${s.subject ? `<span>${esc(s.subject)}</span>` : ""}</div>
    <ol>${nodes}${next}</ol>${trust}</section>`;
}

window.renderConsequences = async function (main) {
  const d = await uiGet("/consequences");
  const sm = d.summary;
  main.innerHTML = `
    <div class="sx cq">
      ${uiHeader("Consequences", "The world remembers. Big decisions come back months later as a meeting, a press conference or an offer, and you choose again.")}
      ${uiKpis([
        { label: "Waiting for you", value: String(sm.waiting), tone: sm.waiting ? "warn" : "" },
        { label: "Stories running", value: String(sm.active) },
        { label: "Choices made", value: String(sm.choices) },
        { label: "Stories ended", value: String(sm.closed) },
      ])}
      ${d.waiting.length ? `<div class="cq-waiting">${d.waiting.map(cqScene).join("")}</div>`
        : uiCard("Nothing to answer right now", `<p class="cq-how">${esc(d.how)}</p>`, { sub: d.stories.length ? "Your stories continue below" : "Make big decisions: they will come back" })}
      ${cqWord(d.word)}
      ${uiCard("Your stories", d.stories.length ? `<div class="cq-tls">${d.stories.map(cqTimeline).join("")}</div>`
        : uiEmpty("No stories yet", "Meet a government, sign or walk away from a deal, raise money, cut costs: someone will remember."),
        { sub: "Each one started with something you did. Finished stories are cleared." })}
    </div>`;

  main.querySelectorAll("[data-cq-option]").forEach((b) => b.addEventListener("click", async () => {
    const scene = b.closest(".cq-scene");
    const result = scene.querySelector("[data-cq-result]");
    scene.querySelectorAll(".cq-btn").forEach((x) => { x.disabled = true; });
    b.classList.add("chosen");
    scene.querySelectorAll(".cq-choice").forEach((c) => c.classList.toggle("faded", !c.contains(b)));
    result.innerHTML = `<p class="cq-pending">…</p>`;
    try {
      const r = await postJSON("/actions/execute", { action: { type: "consequence_choose", params: { chain_id: Number(b.dataset.cqChain), option: b.dataset.cqOption } } });
      result.innerHTML = `<div class="cq-reaction"><div class="cq-face sm" style="--c:${cqColor(scene.querySelector(".cq-who").textContent)}">${esc(cqInitials(scene.querySelector(".cq-who").textContent))}</div><p>${esc(r.message)}</p></div>`;
      setTimeout(() => (typeof window.refreshAll === "function" ? window.refreshAll() : window.renderConsequences(main)), 2200);
    } catch (e) {
      toast(e.message, true);
      result.innerHTML = "";
      scene.querySelectorAll(".cq-choice:not(.short) .cq-btn").forEach((x) => { x.disabled = false; });
      scene.querySelectorAll(".cq-choice").forEach((c) => c.classList.remove("faded"));
      b.classList.remove("chosen");
    }
  }));
};

export {};
